import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  
  RefreshCw, 
  AlertTriangle,
  FileText,
  CheckCircle,
  Filter,
  Check,
  
} from 'lucide-react';
import { getAlerts, acknowledgeAlert, resolveAlert } from '../services/api';
import { Alert } from '../types';
import clsx from 'clsx';
import { format } from 'date-fns';

export default function Alerts() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getAlerts({ 
        status: statusFilter || undefined,
        limit: 50
      });
      if (response.success && response.data) {
        setAlerts(response.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const handleAcknowledge = async (alertId: string) => {
    try {
      setActionLoading(alertId);
      await acknowledgeAlert(alertId);
      fetchData();
    } catch (err: any) {
      alert('Failed: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolve = async (alertId: string) => {
    try {
      setActionLoading(alertId);
      await resolveAlert(alertId);
      fetchData();
    } catch (err: any) {
      alert('Failed: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const severityStyles = {
    info: {
      bg: 'bg-primary-50',
      border: 'border-l-primary-500',
      icon: FileText,
      iconColor: 'text-primary-600'
    },
    warning: {
      bg: 'bg-warning-50',
      border: 'border-l-warning-500',
      icon: AlertTriangle,
      iconColor: 'text-warning-600'
    },
    critical: {
      bg: 'bg-danger-50',
      border: 'border-l-danger-500',
      icon: AlertTriangle,
      iconColor: 'text-danger-600'
    }
  };

  const alertTypeLabels: Record<string, string> = {
    new_challan: 'New Challan',
    overdue: 'Overdue',
    high_amount: 'High Amount',
    high_risk_offence: 'High Risk',
    vehicle_blocked: 'Vehicle Blocked'
  };

  const activeCount = alerts.filter(a => a.status === 'active').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Alerts</h2>
          <p className="text-sm text-gray-500 mt-1">
            {activeCount > 0 ? `${activeCount} active alerts requiring attention` : 'No active alerts'}
          </p>
        </div>
        <button onClick={fetchData} className="btn-secondary">
          <RefreshCw className={clsx("w-4 h-4 mr-2", loading && "animate-spin")} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center gap-4">
          <Filter className="w-4 h-4 text-gray-400" />
          <div className="flex gap-2">
            {['active', 'acknowledged', 'resolved', ''].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  statusFilter === status 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                {status === '' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Alerts List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="card p-12 text-center">
          <CheckCircle className="w-12 h-12 mx-auto text-success-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">All Clear!</h3>
          <p className="text-gray-500">No {statusFilter || ''} alerts at this time</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => {
            const style = severityStyles[alert.severity] || severityStyles.info;
            const Icon = style.icon;

            return (
              <div 
                key={alert.alertId} 
                className={clsx(
                  'card border-l-4 overflow-hidden',
                  style.border,
                  alert.status !== 'active' && 'opacity-60'
                )}
              >
                <div className={clsx('p-4', style.bg)}>
                  <div className="flex items-start gap-4">
                    <div className={clsx('p-2 rounded-lg', style.bg)}>
                      <Icon className={clsx('w-5 h-5', style.iconColor)} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900">{alert.title}</h4>
                        <span className={clsx(
                          'badge',
                          alert.status === 'active' ? 'badge-warning' :
                          alert.status === 'acknowledged' ? 'badge-info' : 'badge-success'
                        )}>
                          {alert.status}
                        </span>
                        <span className="badge bg-gray-100 text-gray-600">
                          {alertTypeLabels[alert.alertType] || alert.alertType}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">{alert.message}</p>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <Link 
                          to={`/vehicles/${alert.vehicleNumber}`}
                          className="text-primary-600 hover:underline"
                        >
                          {alert.vehicleNumber}
                        </Link>
                        <span>
                          {format(new Date(alert.createdAt), 'MMM d, yyyy HH:mm')}
                        </span>
                      </div>
                    </div>

                    {alert.status === 'active' && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleAcknowledge(alert.alertId)}
                          disabled={actionLoading === alert.alertId}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                          title="Acknowledge"
                        >
                          {actionLoading === alert.alertId ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleResolve(alert.alertId)}
                          disabled={actionLoading === alert.alertId}
                          className="p-2 text-gray-500 hover:text-success-600 hover:bg-white rounded-lg transition-colors"
                          title="Resolve"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {alert.status === 'acknowledged' && (
                      <button
                        onClick={() => handleResolve(alert.alertId)}
                        disabled={actionLoading === alert.alertId}
                        className="btn-secondary text-sm py-1"
                      >
                        {actionLoading === alert.alertId ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          'Resolve'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
